// Shared logic for grouping workshop entries coming out of Strapi.
//
// The CMS keeps every edition — regular workshops, teachers' training,
// hackathons, proposal writing — in one flat `workshop_pages` collection.
// These helpers turn that flat list into the grouped, date-aware shape the
// header mega menu, the archive page, the banner and the calendar all need.
//
// Everything degrades gracefully: `category`, `startDate` and `endDate` are
// optional, so the site keeps working while those fields are still being
// filled in inside Strapi.

// Number of most-recent editions shown directly in the menu. Anything older
// than this rolls into that category's archive page automatically.
export const VISIBLE_IN_MENU = 3;

// `key` must keep matching the `category` enumeration in Strapi
// (api/workshop-page/models/workshop-page.settings.json); only `label` is
// display text, and it is reused by the menu, the mobile submenu and the
// archive page tabs.
export const CATEGORIES = [
  { key: "workshop", label: "Workshops" },
  { key: "teacher_training", label: "Teacher Training Workshop" },
  { key: "hackathon", label: "Hackathon" },
  { key: "proposal_writing", label: "Proposal Writing Workshop" },
];

export const categoryLabel = (key) =>
  (CATEGORIES.find((c) => c.key === key) || {}).label || key;

// Work out which column an entry belongs in.
//
// The `category` field set in Strapi always wins. When it isn't set yet we
// fall back to the URL prefix convention already in use (/tt_workshop-1), so
// existing entries land in the right column with no CMS edit required.
export function deriveCategory(item = {}) {
  if (item.category && CATEGORIES.some((c) => c.key === item.category)) {
    return item.category;
  }

  const haystack = `${item.url || ""} ${item.title || ""}`.toLowerCase();

  if (/(^|\/)tt[_-]|teachers?[ _-]training/.test(haystack)) {
    return "teacher_training";
  }
  if (/hackathon/.test(haystack)) return "hackathon";
  if (/(^|\/)pw[_-]|proposal[ _-]writing/.test(haystack)) {
    return "proposal_writing";
  }
  return "workshop";
}

// A stable "which edition is this" number, used to order entries when the
// dates haven't been entered in Strapi yet. /workshop-14 -> 14. Falling back
// to the Strapi id would order wrongly, because ids were not created in
// edition order (tt_workshop-1 is id 36, sitting between workshops 9 and 10).
export function editionRank(item = {}) {
  const match = /-(\d+)\s*$/.exec(item.url || "");
  if (match) return parseInt(match[1], 10);
  return item.id || 0;
}

const time = (value) => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

export const startTime = (item = {}) => time(item.startDate);

// An entry counts as still upcoming until the day its end date passes. When
// only a start date is set we treat that single day as the whole event.
export function isUpcoming(item = {}, now = Date.now()) {
  const end = time(item.endDate) || startTime(item);
  if (end === null) return false;
  // Push to end-of-day so an event is not marked "past" while it is running.
  return end + 24 * 60 * 60 * 1000 > now;
}

// Newest first. Real dates win; entries without dates are ordered by edition
// number and sink below dated ones so a half-filled CMS still reads sensibly.
export function sortNewestFirst(items = []) {
  return items.slice().sort((a, b) => {
    const aStart = startTime(a);
    const bStart = startTime(b);
    if (aStart !== null && bStart !== null) return bStart - aStart;
    if (aStart !== null) return -1;
    if (bStart !== null) return 1;
    return editionRank(b) - editionRank(a);
  });
}

// Group the flat CMS list into the four columns, each already split into the
// editions shown in the menu and the ones rolled into the archive.
export function groupByCategory(items = []) {
  const groups = {};
  CATEGORIES.forEach(({ key }) => {
    groups[key] = { key, label: categoryLabel(key), all: [] };
  });

  items.forEach((item) => {
    const key = deriveCategory(item);
    if (!groups[key]) return;
    groups[key].all.push(item);
  });

  CATEGORIES.forEach(({ key }) => {
    const group = groups[key];
    group.all = sortNewestFirst(group.all);
    group.visible = group.all.slice(0, VISIBLE_IN_MENU);
    group.archived = group.all.slice(VISIBLE_IN_MENU);
    // The archive link only appears once a category actually overflows, so
    // teacher training / hackathon / proposal writing pick it up on their own
    // as soon as they have a fourth edition.
    group.hasArchive = group.archived.length > 0;
    // Drives the blinking NEW tag beside the category name. Needs a real date:
    // an undated entry is never "upcoming".
    group.hasUpcoming = group.all.some((item) => isUpcoming(item));
  });

  return groups;
}

// What the homepage banner's "Upcoming Events" button should point at.
//
// Prefers the next event that has genuinely not happened yet (soonest start
// date first). If no dates are filled in, falls back to the newest workshop
// by edition number, which is what the button used to be hardcoded to.
export function nextUpcoming(items = [], now = Date.now()) {
  const dated = items
    .filter((item) => startTime(item) !== null && isUpcoming(item, now))
    .sort((a, b) => startTime(a) - startTime(b));

  if (dated.length) return dated[0];

  const workshops = sortNewestFirst(
    items.filter((item) => deriveCategory(item) === "workshop")
  );
  return workshops[0] || sortNewestFirst(items)[0] || null;
}

export const isExternal = (url = "") => /^https?:\/\//i.test(url);

// Pull the poster, human-readable dates and venue off a workshop page.
//
// Every edition already carries a `workshop.cover` component in Strapi, so the
// archive can show all three without waiting on the startDate/endDate fields.
// `dates` is free text there ("6 - 8 October, 2026"), which is fine to display
// but useless for sorting — that still goes through startDate/editionRank.
//
// Note this only works on entries from `/workshop-pages`; `/workshop-menu`
// returns titles and urls with no content blocks attached.
export function coverInfo(item = {}) {
  const cover =
    (item.content || []).find((c) => c.__component === "workshop.cover") || {};

  const image = cover.image || {};
  const formats = image.formats || {};
  // Card-sized renditions where Strapi generated them. A couple of the older
  // uploads have no formats at all, so the original is the last resort.
  const source = formats.medium || formats.small || formats.large || image;

  return {
    title: cover.title || item.title,
    venue: cover.venue || null,
    dates: cover.dates || null,
    image: source.url || null,
    alt: image.alternativeText || cover.title || item.title || "",
  };
}
